import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import Edit from "@mui/icons-material/Edit";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  Box,
  Chip,
  IconButton,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FullScreenDocumentPreview } from "components/Document/shared";
import { GetShippingDocumentQuery } from "generated-graphql/graphql";
import { ReactNode, useMemo, useState } from "react";

import { DELETE_WASTE_SHIPPING_DOCUMENT } from "../ShippingDocuments/api";
import { EditShippingDocumentDialog } from "../ShippingDocuments/EditShippingDocumentDialog";
import {
  formatShippingDocumentDate,
  NO_VALUE_PLACEHOLDER,
  SHIPPING_DOCUMENT_KIND_LABELS,
} from "../ShippingDocuments/shippingDocumentDisplay";
import { ShippingDocumentLinesDataGrid } from "../ShippingDocuments/ShippingDocumentLinesDataGrid";
import { SHIPPING_PAPERWORK_QUERY } from "./api";
import { SectionCard } from "./sections/SectionCard";
import { SectionHeader } from "./sections/SectionHeader";
import { SummarySection } from "./sections/SummarySection";
import { TimelineSection } from "./sections/TimelineSection";
import { ShippingDocumentLinkedShipmentCard } from "./ShippingDocumentLinkedShipmentCard";
import { FlowArrow, PARTY_CARD_INSET, ShippingDocumentParties } from "./ShippingDocumentParties";
import {
  buildShippingDocumentSummaryItems,
  buildShippingDocumentTimelineEvents,
} from "./shippingDocumentSummary";

type Document = NonNullable<GetShippingDocumentQuery["wasteShippingDocument"]>;

function FieldValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ px: PARTY_CARD_INSET, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

/**
 * No re-review action here, deliberately. The reading is confirmed once, on
 * ingest, before any record exists — `applyExtractedBillOfLading` is the only
 * path that writes one from a file. A second pass over the same reading cannot
 * set a line's waste stream or container (the review grid has no such column),
 * so it is strictly weaker than Edit for correcting this record, and offering
 * both as equal-weight icons only asked the reader to tell them apart.
 */
/**
 * Delete, behind an overflow menu rather than beside Edit.
 *
 * Two bare icons gave a destructive action the same weight as the ordinary one,
 * on a row the reader passes through every time they open the drawer. Edit is
 * the action they came for; delete is the one they should have to ask for.
 */
function DetailOverflowMenu({
  document,
  onDelete,
  deleting,
}: {
  document: Document;
  onDelete: () => Promise<void>;
  deleting: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <Tooltip title="More options">
        <IconButton
          aria-label="More options"
          size="small"
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setConfirmOpen(true);
          }}
        >
          <ListItemText primary="Delete shipping document" />
        </MenuItem>
      </Menu>
      {confirmOpen && (
        <ConfirmDialog
          open
          onClose={() => setConfirmOpen(false)}
          onConfirm={onDelete}
          header="Delete this shipping document?"
          info={`${document.documentNumber ?? document.id} will be deleted. This cannot be undone.`}
          confirmText="Delete"
          loading={deleting}
        />
      )}
    </>
  );
}

function DetailHeaderRow({
  document,
  onEdit,
  onDelete,
  onClose,
  deleting,
}: {
  document: Document;
  onEdit: () => void;
  onDelete: () => Promise<void>;
  onClose: () => void;
  deleting: boolean;
}) {
  return (
    // Sticky, so the document number stays on screen while the lines grid
    // scrolls, matching StickyManifestHeader. The rule runs the full width of
    // the drawer, so the padding sits inside this Box rather than on a parent.
    // `pt` is lighter than `pb` because this is the drawer's first row.
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        bgcolor: "background.paper",
        px: 3,
        pt: 1.5,
        pb: 2,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        {/* Title row, then chips beneath it. The manifest header is shaped the
            same way, so a second chip lands on its own line rather than pushing
            the title around. */}
        <Stack spacing={0.5} minWidth={0}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="h5">{document.documentNumber ?? "Shipping document"}</Typography>
            <Tooltip title="Edit shipping document">
              <IconButton aria-label="Edit shipping document" size="small" onClick={onEdit}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={SHIPPING_DOCUMENT_KIND_LABELS[document.kind]} size="small" />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <DetailOverflowMenu document={document} onDelete={onDelete} deleting={deleting} />
          <Tooltip title="Close">
            <IconButton aria-label="Close" size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * The source document's title, as a link only when there is a file to open.
 * `FullScreenDocumentPreview` mounts on `downloadLink`, so linking on the
 * presence of the record alone gave a clickable title that opened nothing and
 * said nothing about why.
 */
function SourceDocumentValue({
  document,
  onPreview,
}: {
  document: Document;
  onPreview: () => void;
}) {
  const sourceDocument = document.sourceDocument;
  if (!sourceDocument) return <>{NO_VALUE_PLACEHOLDER}</>;
  if (!sourceDocument.downloadLink) {
    return (
      <Tooltip title="This document has no file to preview">
        <span>{sourceDocument.title}</span>
      </Tooltip>
    );
  }
  return (
    <Link component="button" onClick={onPreview}>
      {sourceDocument.title}
    </Link>
  );
}

/**
 * The document's own three fields, as a row sized to sit directly above the
 * party cards: same Stack direction, spacing, flex:1 items and FlowArrow-width
 * spacers. The party row's arrows shift when a card is missing, so this only
 * lines up pixel-for-pixel in the three-card case; with fewer cards it still
 * reads as three even fields above a shorter row.
 */
function DocumentFieldsRow({ document, onPreview }: { document: Document; onPreview: () => void }) {
  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
      <FieldValue label="Ship date">{formatShippingDocumentDate(document.shipDate)}</FieldValue>
      <FlowArrow invisible />
      <FieldValue label="Created">{formatShippingDocumentDate(document.createdAt)}</FieldValue>
      <FlowArrow invisible />
      <FieldValue label="Source document">
        <SourceDocumentValue document={document} onPreview={onPreview} />
      </FieldValue>
    </Stack>
  );
}

/**
 * The detail body for one shipping document: header actions, the field grid,
 * and the lines table. The caller resolves the document and owns the shell —
 * `ShippingDocumentDrawer` is the only consumer, and this component takes no
 * route params of its own.
 */
export function ShippingDocumentDetailContent({
  tenantId,
  document,
  onDeleted,
  onClose,
}: {
  tenantId: string;
  document: Document;
  /** Called after a successful delete, so the drawer can close itself. */
  onDeleted: () => void;
  /** Closes the drawer. Rendered in the header row, not above it. */
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const [editOpen, setEditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const summaryItems = useMemo(() => buildShippingDocumentSummaryItems(document), [document]);
  const timelineEvents = useMemo(() => buildShippingDocumentTimelineEvents(document), [document]);

  // The merged paperwork list is the only surface that renders these rows, so
  // it is the only query worth refetching.
  const [deleteDocument, { loading: deleting }] = useMutation(DELETE_WASTE_SHIPPING_DOCUMENT, {
    refetchQueries: [SHIPPING_PAPERWORK_QUERY],
  });

  const handleDelete = async () => {
    try {
      await deleteDocument({ variables: { id: document.id } });
      alerts.success("Shipping document deleted");
      onDeleted();
    } catch (error) {
      alerts.error(
        "Could not delete the shipping document",
        error instanceof Error ? error.message : undefined
      );
    }
  };

  return (
    <>
      <DetailHeaderRow
        document={document}
        onEdit={() => setEditOpen(true)}
        onDelete={handleDelete}
        onClose={onClose}
        deleting={deleting}
      />

      <Stack spacing={3} px={3} py={2} sx={{ flex: 1, overflow: "auto" }}>
        {/* Summary beside Timeline, the pair the manifest drawer opens with:
            what the lines are missing on the left, where the record has got to
            on the right. Everything below is full width. */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="stretch">
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <SectionCard>
              <SummarySection items={summaryItems} />
            </SectionCard>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <SectionCard>
              <TimelineSection events={timelineEvents} />
            </SectionCard>
          </Box>
        </Stack>

        {/* One card for what the paper says about the shipment: its three
            fields, then the generator to transporter to destination flow. The
            flow needs the drawer's full width for its arrows, and the fields
            were shaped to sit above it. */}
        <SectionCard>
          <SectionHeader title="Shipping" />
          <Stack spacing={2}>
            <DocumentFieldsRow document={document} onPreview={() => setPreviewOpen(true)} />
            <ShippingDocumentParties document={document} />
          </Stack>
        </SectionCard>

        <SectionCard>
          <SectionHeader title="Lines" />
          <ShippingDocumentLinesDataGrid tenantId={tenantId} shippingDocumentId={document.id} />
        </SectionCard>

        {/* Container tracking's view of this document: the containers that went
            out on it, or the open shipments it might belong to. Renders itself
            away entirely when the tenant is not on container tracking, or when
            there is neither a link nor a plausible candidate. Last, matching
            where the manifest drawer puts its copy. */}
        <ShippingDocumentLinkedShipmentCard shippingDocumentId={document.id} tenantId={tenantId} />

        {editOpen && (
          <EditShippingDocumentDialog
            open
            onClose={() => setEditOpen(false)}
            document={document}
            onSaved={() => setEditOpen(false)}
          />
        )}

        {document.sourceDocument?.downloadLink && (
          <FullScreenDocumentPreview
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            fileUrl={document.sourceDocument.downloadLink}
            fileExtension={document.sourceDocument.fileExtension ?? undefined}
            title={document.sourceDocument.title ?? "Document Preview"}
          />
        )}
      </Stack>
    </>
  );
}
